/**
 * Every sound behind one small API in world coordinates (GDD §9): the game glue (src/audio/gameAudio.ts) and the
 * sound check page drive it. Built once on any BaseAudioContext; nothing it does per frame creates audio nodes or
 * JavaScript objects (GDD §10.5). The listener is the jet, facing upriver (judgment call 43): positions are sent to
 * the panners relative to it, so the 800 m world shift never moves a sound.
 */
import { createEngineVoice, createLoopVoice, createSignals, setEngine, setLoop, setSignals } from './loops.ts';
import type { EngineVoice, LoopKind, LoopVoice, Signals } from './loops.ts';
import { createMixer, duck, setDemo, setVolumes, type Mixer } from './mixer.ts';
import { createMusic, scheduleMusic, type Music } from './music.ts';
import { playSfx, SFX_KINDS, type SfxKind } from './sfx.ts';
import { createSlot, glide, LEAD, noiseBuffer, setAt, takeSlot, type Slot } from './voices.ts';

/** Positional one-shot voices, cannon voices (alternating, so a tail isn't cut) and one interface voice. */
export const SLOTS = 10;
const CANNON_SLOTS = 2;
/** A positional sound's panner moves once it has drifted this far from where it was sent (m). */
const MOVE = 0.5;
/** Continuous enemy voices per kind. */
export const LOOP_COUNT: Record<LoopKind, number> = { heli: 3, ship: 2, jet: 2 };

export interface SoundStats {
  /** One-shots played, by kind. */
  played: Record<SfxKind, number>;
  /** Positional slots sounding. */
  busy: number;
}

export interface SoundEngine {
  readonly ctx: BaseAudioContext;
  readonly mixer: Mixer;
  readonly engineVoice: EngineVoice;
  readonly loops: Record<LoopKind, LoopVoice[]>;
  readonly signals: Signals;
  readonly music: Music;
  readonly stats: SoundStats;
  /** The context's time, read once per frame by begin(). */
  readonly now: number;
  /** Starts a frame: reads the audio clock once. */
  begin(): number;
  volumes(music: number, effects: number, mute: boolean): void;
  demo(on: boolean): void;
  /** The jet's world position. */
  listener(x: number, y: number, z: number): void;
  /** The world moved by `dz` (floating origin): sounds keep their place. */
  shift(dz: number): void;
  /** A one-shot at a world position; `size` for explosions and splashes. */
  play(kind: SfxKind, x: number, y: number, z: number, size: number): void;
  /** A one-shot at the jet (cannon, chime), `delay` seconds from now. */
  local(kind: SfxKind, delay: number): void;
  engine(speed: number, alive: boolean): void;
  /** Voice `i` of a kind follows `owner` at (rx, ry, rz) from the jet, with Doppler `d`; null fades it out. */
  loop(kind: LoopKind, i: number, owner: object | null, rx: number, ry: number, rz: number, d: number): void;
  setSignals(refuel: boolean, alarm: boolean, fuel: number): void;
  setMusic(intensity: number): void;
  duck(): void;
  /** End of a frame: positional sounds follow the jet's flight past them (`track`: every other frame is enough). */
  update(track: boolean): void;
}

export function createSoundEngine(ctx: BaseAudioContext): SoundEngine {
  const mixer = createMixer(ctx);
  const noise = noiseBuffer(ctx);
  const slots: Slot[] = [];
  for (let i = 0; i < SLOTS; i++) slots.push(createSlot(ctx, noise, mixer.effects, true));
  const cannon: Slot[] = [];
  for (let i = 0; i < CANNON_SLOTS; i++) cannon.push(createSlot(ctx, noise, mixer.effects, false));
  const ui = createSlot(ctx, noise, mixer.effects, false);
  const engineVoice = createEngineVoice(ctx, noise, mixer.effects);
  const loops: Record<LoopKind, LoopVoice[]> = { heli: [], ship: [], jet: [] };
  for (const kind of ['heli', 'ship', 'jet'] as const)
    for (let i = 0; i < LOOP_COUNT[kind]; i++) loops[kind].push(createLoopVoice(ctx, noise, mixer.effects, kind, i));
  const signals = createSignals(ctx, mixer.effects);
  const music = createMusic(ctx, noise, mixer.music);
  const played = {} as Record<SfxKind, number>;
  for (const k of SFX_KINDS) played[k] = 0;
  const stats: SoundStats = { played, busy: 0 };
  // Numbers updated in place every frame.
  const st = { now: 0, lx: 0.5, ly: 0.5, lz: 0.5, cannon: 0 };

  const api: SoundEngine = {
    ctx,
    mixer,
    engineVoice,
    loops,
    signals,
    music,
    stats,
    get now() {
      return st.now;
    },
    begin() {
      st.now = ctx.currentTime;
      return st.now;
    },
    volumes(m, e, mute) {
      setVolumes(mixer, st.now, m, e, mute);
    },
    demo(on) {
      setDemo(mixer, st.now, on);
    },
    listener(x, y, z) {
      st.lx = x;
      st.ly = y;
      st.lz = z;
    },
    shift(dz) {
      st.lz += dz;
      for (let i = 0; i < slots.length; i++) (slots[i] as Slot).z += dz;
    },
    play(kind, x, y, z, size) {
      const now = ctx.currentTime;
      const s = takeSlot(slots, now);
      s.x = x;
      s.y = y;
      s.z = z;
      s.rx = x - st.lx;
      s.ry = y - st.ly;
      s.rz = z - st.lz;
      const p = s.pan as PannerNode;
      setAt(p.positionX, now, now, s.rx);
      setAt(p.positionY, now, now, s.ry);
      setAt(p.positionZ, now, now, s.rz);
      const t = now + LEAD;
      s.busyUntil = t + playSfx(kind, s, now, t, size, y);
      played[kind]++;
    },
    local(kind, delay) {
      const now = ctx.currentTime;
      const s = kind === 'cannon' ? (cannon[st.cannon++ % CANNON_SLOTS] as Slot) : ui;
      const t = now + LEAD + delay;
      s.busyUntil = t + playSfx(kind, s, now, t, 1, 0);
      played[kind]++;
    },
    engine(speed, alive) {
      setEngine(engineVoice, st.now, speed, alive);
    },
    loop(kind, i, owner, rx, ry, rz, d) {
      const v = loops[kind][i];
      if (v) setLoop(v, st.now, owner, rx, ry, rz, d);
    },
    setSignals(refuel, alarm, fuel) {
      setSignals(signals, st.now, refuel, alarm, fuel);
    },
    setMusic(intensity) {
      scheduleMusic(music, st.now, intensity);
    },
    duck() {
      duck(mixer, st.now);
    },
    update(track) {
      const now = st.now;
      let busy = 0;
      for (let i = 0; i < slots.length; i++) {
        const s = slots[i] as Slot;
        if (s.busyUntil <= now) continue;
        busy++;
        if (!track) continue;
        // Only what moved by more than half a metre: panning can't tell less.
        const p = s.pan as PannerNode;
        const rx = s.x - st.lx;
        const ry = s.y - st.ly;
        const rz = s.z - st.lz;
        if (Math.abs(rx - s.rx) > MOVE) glide(p.positionX, now, (s.rx = rx), 0.05);
        if (Math.abs(ry - s.ry) > MOVE) glide(p.positionY, now, (s.ry = ry), 0.05);
        if (Math.abs(rz - s.rz) > MOVE) glide(p.positionZ, now, (s.rz = rz), 0.05);
      }
      stats.busy = busy;
    },
  };
  return api;
}
